<template>
  <div class="scan-stock-in-container">
    <!-- 原有页面内容完全保留 -->
    <header class="header">
      <button @click="handleBack" class="back-btn" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </button>
      <h1 class="title">扫码入库</h1>
    </header>

    <main class="content">
      <!-- 状态提示 -->
      <div v-if="statusMsg" class="status-banner" :class="statusType">
        {{ statusMsg }}
      </div>

      <!-- 源数据输入区 -->
      <div class="form-section source-section">
        <h3 class="section-title">源数据输入</h3>
        <div class="form-group">
          <input 
            id="sourceDataInput"
            v-model="sourceData"
            placeholder="扫描数据后自动解析"
            @input="handleInputChange"
            autocomplete="off"
          />
        </div>
        
        <!-- 按钮布局 -->
        <div class="source-actions">
          <button @click="handleSourceParse" class="btn parse-btn" :disabled="isParsing">
            <i class="icon-parse"></i> {{ isParsing ? '解析中...' : '解析' }}
          </button>
          <button @click="openManualInput" class="btn manual-input-btn">
            <i class="icon-manual"></i> 手动输入
          </button>
          <button @click="clearSourceData" class="btn clear-btn">
            <i class="icon-clear"></i> 清空
          </button>
        </div>
      </div>

      <!-- 解析后卡片列表 -->
      <div class="form-section card-section">
        <h3 class="section-title">入库数据列表</h3>
        
        <!-- 选中仓库信息展示 -->
        <div v-if="selectedWarehouse" class="warehouse-info-bar">
          <span class="warehouse-label">当前入库仓库：</span>
          <span class="warehouse-value">{{ selectedWarehouse.wareName }}</span>
          <button @click="reselectWarehouse" class="reselect-btn">更换仓库</button>
        </div>

        <!-- 数据汇总 -->
        <div v-if="parsedList.length > 0" class="summary-bar">
          <div class="summary-item">
            <span class="summary-label">总箱数：</span>
            <span class="summary-value">{{ parsedList.length }} 箱</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">总件数：</span>
            <span class="summary-value text-primary">{{ totalQuantity.toFixed(2) }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">物料种类：</span>
            <div class="material-details">
              <div v-for="material in materialDetails" :key="material.code" class="material-item">
                {{ material.code }}  ====  ({{ material.count }}个)
              </div>
            </div>
          </div>
        </div>

        <!-- 空状态 -->
        <div class="empty-state" v-if="parsedList.length === 0">
          <div class="empty-icon">📦</div>
          <p class="empty-text">暂无入库数据，请扫描或手动输入</p>
        </div>

        <!-- 数据卡片列表 -->
        <div class="card-list" v-else>
          <div class="data-card" v-for="(item, index) in parsedList" :key="`${item.batchNo}-${index}`">
            <div class="card-header">
              <span class="card-label">箱号：{{ item.batchNo }}</span>
              <button @click="removeCard(index)" class="card-delete" aria-label="删除">×</button>
            </div>
            <div class="card-body">
              <div class="card-row">
                <span class="row-label">物料编码：</span>
                <span class="row-value">{{ item.materialNo }}</span>
              </div>
              <div class="card-row">
                <span class="row-label">供应商编码：</span>
                <span class="row-value">{{ item.supplierCode }}</span>
              </div>
              <div class="card-row">
                <span class="row-label">入库数量：</span>
                <span class="row-value text-primary">{{ Number(item.wareInNum).toFixed(2) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 提交按钮和查看状态按钮 -->
        <div class="action-buttons">
          <button 
            @click="handleSubmit"
            :disabled="!canSubmit"
            class="submit-btn"
          >
            预览
          </button>
          
          <button 
            @click="openTaskStatusModal"
            class="btn status-btn"
            :disabled="backgroundTaskIds.length === 0"
          >
            <i class="icon-status"></i> 查看入库状态
          </button>
        </div>
      </div>
    </main>

    <!-- 手动输入弹窗 -->
    <div v-if="showManualInput" class="modal-overlay">
      <!-- 内容保持不变 -->
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">手动输入入库数据</h3>
          <button @click="closeManualInput" class="modal-close" aria-label="关闭">×</button>
        </div>
        <div class="modal-body" style="flex: 1; overflow-y: auto; padding-bottom: 20px;">
          <div class="form-group">
            <label for="batchNo">箱号 <span class="required">*</span></label>
            <input 
              id="batchNo" 
              v-model="manualInput.batchNo" 
              type="text" 
              placeholder="请输入箱号ID"
              @input="trimInput('batchNo')"
            >
          </div>
          <div class="form-group">
            <label for="supplierCode">供应商编码 <span class="required">*</span></label>
            <input 
              id="supplierCode" 
              v-model="manualInput.supplierCode" 
              type="text" 
              placeholder="请输入供应商编码"
              @input="trimInput('supplierCode')"
              @blur="handleSupplierCodeBlur"
            >
          </div>
          <div class="form-group">
            <label for="materialNo">物料编码 <span class="required">*</span></label>
            <input 
              id="materialNo" 
              v-model="manualInput.materialNo" 
              type="text" 
              placeholder="请输入物料编码"
              @input="trimInput('materialNo')"
              @blur="handleMaterialNoBlur"
            >
          </div>
          <div class="form-group">
            <label for="wareInNum">入库数量 <span class="required">*</span></label>
            <input 
              id="wareInNum"
              v-model="manualInput.wareInNum"
              type="number"
              min="0"
              step="0.01"
              placeholder="请输入数量（保留两位小数）"
              @input="formatWareInNum"
            >
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeManualInput" class="btn modal-cancel">取消</button>
          <button @click="confirmManualInput" class="btn modal-confirm" :disabled="!isManualInputValid">
            确认添加
          </button>
        </div>
      </div>
    </div>

    <!-- 预览弹窗 -->
    <div v-if="showPreview" class="modal-overlay">
      <!-- 内容保持不变 -->
      <div class="modal-container pda-optimized" style="max-height: 90vh; width: 95vw; max-width: 800px; display: flex; flex-direction: column;">
        <div class="modal-header">
          <h3 class="modal-title">入库数据确认</h3>
          <button @click="closePreview" class="modal-close" aria-label="关闭">×</button>
        </div>
        <div class="modal-body">
          <!-- 入库汇总信息 -->
          <div class="preview-summary">
            <h4 class="summary-title">入库汇总信息</h4>
            
            <!-- 汇总表格 -->
            <div class="summary-table-container">
              <table class="summary-table">
                <thead>
                  <tr>
                    <th class="summary-header">供应商</th>
                    <th class="summary-header">物料编码</th>
                    <th class="summary-header">批次数量</th>
                    <th class="summary-header">总数量</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="summary-row" v-for="(item, index) in groupedSummary" :key="index">
                    <td class="summary-cell supplier-cell">{{ item.supplier }}</td>
                    <td class="summary-cell material-cell">{{ item.materialNo }}</td>
                    <td class="summary-cell">{{ item.batchCount }}</td>
                    <td class="summary-cell total-cell">{{ Number(item.totalQuantity).toFixed(2) }}</td>
                  </tr>
                  <!-- 总计行 -->
                  <tr class="summary-row total-row">
                    <td class="summary-cell" colspan="2"><strong>总计</strong></td>
                    <td class="summary-cell"><strong>{{ parsedList.length }}</strong></td>
                    <td class="summary-cell total-cell"><strong>{{ totalQuantity.toFixed(2) }}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- 入库明细 -->
          <div class="preview-detail" style="margin-top: 20px;">
            <h4 class="detail-title">入库明细（共 {{ parsedList.length }} 箱）</h4>
            
            <div class="detail-cards">
              <div class="detail-card" v-for="(item, index) in parsedList" :key="`detail-${item.batchNo}-${index}`">
                <div class="detail-card-header">
                  <span class="detail-card-title">箱号：{{ item.batchNo }}</span>
                </div>
                <div class="detail-card-body">
                  <div class="detail-item">
                    <span class="detail-label">物料编码：</span>
                    <span class="detail-value">{{ item.materialNo }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">供应商：</span>
                    <span class="detail-value">{{ item.supplierName || item.supplierCode }}</span>
                  </div>
                  <div class="detail-item highlight-item">
                    <span class="detail-label">入库数量：</span>
                    <span class="detail-value">{{ Number(item.wareInNum).toFixed(2) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closePreview" class="btn modal-cancel">返回</button>
          <button @click="confirmStockIn" class="btn modal-confirm" :disabled="isSubmitting">
            <span v-if="!isSubmitting">提交入库</span>
            <span v-if="isSubmitting">提交中...</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 供应商不存在提示弹窗 -->
    <div v-if="showSupplierNotFoundModal" class="modal-overlay">
      <!-- 内容保持不变 -->
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">提示</h3>
          <button @click="closeSupplierNotFoundModal" class="modal-close" aria-label="关闭">×</button>
        </div>
        <div class="modal-body">
          <p class="error-message">{{ supplierNotFoundMsg }}</p>
          <p class="error-tip">请检查供应商编码是否正确，或联系管理员添加该供应商</p>
        </div>
        <div class="modal-footer">
          <button @click="closeSupplierNotFoundModal" class="btn modal-confirm">确定</button>
        </div>
      </div>
    </div>

    <!-- 物料不存在提示弹窗 -->
    <div v-if="showMaterialNotFoundModal" class="modal-overlay">
      <!-- 内容保持不变 -->
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">提示</h3>
          <button @click="closeMaterialNotFoundModal" class="modal-close" aria-label="关闭">×</button>
        </div>
        <div class="modal-body">
          <p class="error-message">{{ materialNotFoundMsg }}</p>
          <p class="error-tip">请检查物料编码是否正确，或确认该物料已关联当前供应商</p>
        </div>
        <div class="modal-footer">
          <button @click="closeMaterialNotFoundModal" class="btn modal-confirm">确定</button>
        </div>
      </div>
    </div>

    <!-- 仓库选择弹窗 -->
    <div v-if="showWarehouseSelectModal" class="modal-overlay">
      <!-- 内容保持不变 -->
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">选择入库仓库</h3>
          <button @click="closeWarehouseSelectModal" class="modal-close" aria-label="关闭" :disabled="isLoadingWarehouses">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label for="warehouseSelect">仓库列表 <span class="required">*</span></label>
            <select 
              id="warehouseSelect" 
              v-model="selectedWarehouseId" 
              class="warehouse-select"
              :disabled="isLoadingWarehouses"
            >
              <option value="" disabled v-if="!isLoadingWarehouses">请选择仓库</option>
              <option value="" disabled v-if="isLoadingWarehouses">加载仓库列表中...</option>
              <option 
                v-for="warehouse in warehouseList" 
                :key="warehouse.id" 
                :value="warehouse.id"
              >
                {{ warehouse.wareName }}（编码：{{ warehouse.wareNo }}）
              </option>
              <option value="" disabled v-if="!isLoadingWarehouses && warehouseList.length === 0">暂无可用仓库</option>
            </select>
          </div>
          <p v-if="!isLoadingWarehouses && warehouseList.length === 0" class="empty-warehouse-text">
            ⚠️ 未查询到可用仓库，请联系管理员添加仓库信息后再操作
          </p>
        </div>
        <div class="modal-footer">
          <button 
            @click="closeWarehouseSelectModal" 
            class="btn modal-cancel"
            :disabled="isLoadingWarehouses"
          >
            取消
          </button>
          <button 
            @click="confirmWarehouseSelect" 
            class="btn modal-confirm"
            :disabled="isLoadingWarehouses || !selectedWarehouseId"
          >
            确认选择
          </button>
        </div>
      </div>
    </div>

    <!-- 错误提示弹窗 -->
    <div v-if="showErrorModal" class="modal-overlay">
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">{{ errorModalTitle }}</h3>
          <button @click="showErrorModal = false" class="modal-close" aria-label="关闭">×</button>
        </div>
        <div class="modal-body">
          <p class="error-message">{{ errorModalDetail }}</p>
          <p v-if="errorModalAdvice" class="error-tip">{{ errorModalAdvice }}</p>
        </div>
        <div class="modal-footer">
          <button @click="handleErrorModalAction" class="btn modal-confirm">{{ errorModalActionText }}</button>
        </div>
      </div>
    </div>

    <!-- 入库状态查看弹窗 -->
    <div v-if="showTaskStatusModal" class="modal-overlay">
      <!-- 内容保持不变 -->
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">入库任务状态</h3>
          <button @click="closeTaskStatusModal" class="modal-close" aria-label="关闭">×</button>
        </div>
        <div class="modal-body">
          <div class="task-status-header">
            <div class="task-status-filters">
              <button 
                @click="refreshTaskStatus" 
                class="btn refresh-btn"
                :disabled="isRefreshingStatus"
              >
                <i class="icon-refresh"></i> {{ isRefreshingStatus ? '刷新中...' : '刷新状态' }}
              </button>
            </div>
            <div class="task-status-summary">
              <div class="status-count success-count">
                <span class="count-label">成功：</span>
                <span class="count-value">{{ successTaskCount }}</span>
              </div>
              <div class="status-count processing-count">
                <span class="count-label">处理中：</span>
                <span class="count-value">{{ processingTaskCount }}</span>
              </div>
              <div class="status-count error-count">
                <span class="count-label">失败：</span>
                <span class="count-value">{{ errorTaskCount }}</span>
              </div>
            </div>
          </div>
          
          <div class="task-list" v-if="Object.keys(backgroundTaskStatus).length > 0">
            <div class="task-item" v-for="(status, wareInId) in backgroundTaskStatus" :key="wareInId">
              <div class="task-id">入库单ID：{{ wareInId }}</div>
              <div class="task-status" :class="status">
                <span v-if="status === 'success'">✅ 已完成</span>
                <span v-if="status === 'processing'">⌛ 处理中</span>
                <span v-if="status === 'error'">❌ 失败</span>
              </div>
              <div class="task-error" v-if="taskErrors[wareInId]">
                {{ taskErrors[wareInId] }}
              </div>
            </div>
          </div>
          
          <div class="empty-state" v-else>
            <div class="empty-icon">📋</div>
            <p class="empty-text">暂无入库任务记录</p>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeTaskStatusModal" class="btn modal-confirm">关闭</button>
        </div>
      </div>
    </div>

    <!-- 核心变更：入库进度弹窗 -->
    <div v-if="isSubmitting" class="modal-overlay">
      <div class="modal-container progress-modal">
        <div class="modal-header">
          <h3 class="modal-title">入库处理中</h3>
          <!-- 不提供关闭按钮，防止误操作中断流程 -->
        </div>
        
        <div class="modal-body progress-body">
          <div class="progress-stats">
            <span class="stat-item success">
              <span class="stat-icon">✅</span> 成功: {{ currentSuccessCount }}
            </span>
            <span class="stat-item processing">
              <span class="stat-icon">⌛</span> 处理中: {{ currentProcessingCount }}
            </span>
            <span class="stat-item error">
              <span class="stat-icon">❌</span> 失败: {{ currentErrorCount }}
            </span>
            <span class="stat-item total">
              <span class="stat-icon">📊</span> 总计: {{ totalSuppliers }}
            </span>
          </div>
          
          <div class="progress-list">
            <div 
              class="progress-item" 
              v-for="(item, index) in processingProgress" 
              :key="index"
              :class="item.status"
            >
              <div class="progress-item-header">
                <span class="supplier-name">{{ item.supplierName || `供应商 ${index + 1}` }}</span>
                <span class="status-badge">
                  <span v-if="item.status === 'success'">✅ 已完成</span>
                  <span v-if="item.status === 'processing'">⌛ 处理中</span>
                  <span v-if="item.status === 'error'">❌ 失败</span>
                </span>
              </div>
              <div class="progress-item-details">
                <div class="batch-info">
                  批次数量: {{ item.batchCount }} 箱
                </div>
                <div class="error-message" v-if="item.status === 'error' && item.errorMsg">
                  {{ item.errorMsg }}
                </div>
                <div class="progress-bar" v-if="item.status === 'processing'">
                  <div class="progress-indicator" :style="{ width: item.progress + '%' }"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button 
            @click="cancelSubmission" 
            class="btn cancel-btn"
            :disabled="!canCancel"
          >
            {{ canCancel ? '取消' : '处理中...' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, nextTick, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import * as stockInApi from '@/api/stockIn'

export default {
  name: 'ScanStockIn',
  setup() {
    // 所有业务逻辑与之前完全一致，未做任何修改
    const router = useRouter()

    // 音频上下文相关变量
    let audioContext = null
    const isAudioContextActive = ref(false)

    // 基础响应式变量
    const sourceData = ref('')
    const parsedList = ref([])
    const statusMsg = ref('')
    const statusType = ref('success')
    const isParsing = ref(false)
    const isSubmitting = ref(false)
    
    // 入库进度跟踪变量
    const processingProgress = ref([])
    const totalSuppliers = ref(0)
    const currentSuccessCount = ref(0)
    const currentProcessingCount = ref(0)
    const currentErrorCount = ref(0)
    const canCancel = ref(true)
    const submissionAborted = ref(false)

    // 后台任务状态管理
    const backgroundTaskIds = ref([])
    const backgroundTaskStatus = ref({})
    const taskErrors = ref({})
    const showTaskStatusModal = ref(false)
    const isRefreshingStatus = ref(false)

    // 仓库相关变量
    const warehouseList = ref([])
    const selectedWarehouse = ref(null)
    const selectedWarehouseId = ref('')
    const showWarehouseSelectModal = ref(false)
    const isLoadingWarehouses = ref(false)

    // 弹窗控制变量
    const showManualInput = ref(false)
    const showPreview = ref(false)
    const showSupplierNotFoundModal = ref(false)
    const showMaterialNotFoundModal = ref(false)

    // 手动输入表单变量
    const manualInput = ref({
      materialNo: '',
      materialId: '',
      materialGroupId: '',
      wareInNum: 1.00,
      supplierCode: '',
      supplierId: '',
      batchNo: '',
      remark: ''
    })

    // 错误提示消息
    const supplierNotFoundMsg = ref('')
    const materialNotFoundMsg = ref('')

    // 当前时间和用户信息
    const currentDateTime = computed(() => {
      const now = new Date()
      return now.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }).replace(/\//g, '-')
    })
    
    const currentUser = ref('张三')

    // 错误弹窗控制变量
    const showErrorModal = ref(false)
    const errorModalTitle = ref('')
    const errorModalDetail = ref('')
    const errorModalAdvice = ref('')
    const errorModalActionText = ref('确定')
    const errorModalAction = ref(null)

    // 错误弹窗显示函数
    const showErrorDialog = (title, detail, advice = '', actionText = '确定', action = null) => {
      if (showErrorModal.value) return
      errorModalTitle.value = title
      errorModalDetail.value = detail
      errorModalAdvice.value = advice
      errorModalActionText.value = actionText
      errorModalAction.value = action;
      showErrorModal.value = true
    }

    // 初始化并激活 AudioContext
    const initAudioContext = () => {
      if (audioContext || isAudioContextActive.value) return

      try {
        const AudioContextConstructor = window.AudioContext || window.webkitAudioContext
        audioContext = new AudioContextConstructor()
        
        if (audioContext.state === 'suspended') {
          audioContext.resume().then(() => {
            isAudioContextActive.value = true
            console.log('AudioContext 已激活')
          }).catch(err => {
            console.warn('激活 AudioContext 失败：', err)
          })
        } else {
          isAudioContextActive.value = true
        }

        document.removeEventListener('click', initAudioContext)
        document.removeEventListener('touchstart', initAudioContext)
      } catch (err) {
        console.warn('创建 AudioContext 失败：', err)
      }
    }

    // 失败提示音
    const playErrorBeep = (count = 5) => {
      try {
        if (!audioContext) {
          console.warn('AudioContext 未初始化，尝试临时创建')
          const AudioContextConstructor = window.AudioContext || window.webkitAudioContext
          audioContext = new AudioContextConstructor()
        }

        if (audioContext.state === 'suspended') {
          audioContext.resume().catch(err => {
            console.warn('恢复 AudioContext 失败：', err)
            return
          })
        }

        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()
        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)

        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(2200, audioContext.currentTime)
        gainNode.gain.setValueAtTime(2, audioContext.currentTime)

        const beepInterval = 200
        for (let i = 0; i < count; i++) {
          setTimeout(() => {
            gainNode.gain.setValueAtTime(2, audioContext.currentTime)
            setTimeout(() => {
              gainNode.gain.setValueAtTime(0, audioContext.currentTime)
            }, 100)
          }, i * beepInterval)
        }

        oscillator.start()
        oscillator.onended = () => {
          oscillator.disconnect()
          gainNode.disconnect()
        }
      } catch (error) {
        console.warn('播放提示音失败：', error)
      }
    };

    // 成功提示音
    const playSuccessBeep = () => {
      try {
        if (!audioContext) {
          console.warn('AudioContext 未初始化，尝试临时创建')
          const AudioContextConstructor = window.AudioContext || window.webkitAudioContext
          audioContext = new AudioContextConstructor()
        }

        if (audioContext.state === 'suspended') {
          audioContext.resume().catch(err => {
            console.warn('恢复 AudioContext 失败：', err)
            return
          })
        }

        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()
        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)

        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(3000, audioContext.currentTime)
        gainNode.gain.setValueAtTime(1, audioContext.currentTime)

        oscillator.start()
        oscillator.stop(audioContext.currentTime + 0.1)
        oscillator.onended = () => {
          oscillator.disconnect()
          gainNode.disconnect()
        }
      } catch (error) {
        console.warn('播放成功提示音失败：', error)
      }
    };

    // 计算属性
    const totalQuantity = computed(() => {
      const sum = parsedList.value.reduce((total, item) => {
        return Number((total + Number(item.wareInNum)).toFixed(2))
      }, 0)
      return sum
    })

    const uniqueMaterialCount = computed(() => {
      const materialSet = new Set(parsedList.value.map(item => item.materialNo))
      return materialSet.size
    })

    // 新增计算属性：物料详情，包含编码和对应数量
    const materialDetails = computed(() => {
      const materialMap = new Map()
      parsedList.value.forEach(item => {
        const currentCount = materialMap.get(item.materialNo) || 0
        materialMap.set(item.materialNo, currentCount + 1)
      })
      // 转换为数组格式便于展示
      return Array.from(materialMap.entries()).map(([code, count]) => ({
        code,
        count
      }))
    })

    const uniqueSupplierCount = computed(() => {
      const supplierSet = new Set(parsedList.value.map(item => item.supplierCode))
      return supplierSet.size
    })

    const groupedSummary = computed(() => {
      const groups = {}
      
      parsedList.value.forEach(item => {
        const key = `${item.supplierCode || item.supplierName}-${item.materialNo}`
        
        if (!groups[key]) {
          groups[key] = {
            supplier: item.supplierName || item.supplierCode,
            materialNo: item.materialNo,
            batchCount: 0,
            totalQuantity: 0
          }
        }
        
        groups[key].batchCount += 1
        groups[key].totalQuantity = Number((groups[key].totalQuantity + item.wareInNum).toFixed(2))
      })
      
      return Object.values(groups)
    })

    // 入库任务状态统计
    const successTaskCount = computed(() => {
      return Object.values(backgroundTaskStatus.value).filter(status => status === 'success').length
    })
    
    const processingTaskCount = computed(() => {
      return Object.values(backgroundTaskStatus.value).filter(status => status === 'processing').length
    })
    
    const errorTaskCount = computed(() => {
      return Object.values(backgroundTaskStatus.value).filter(status => status === 'error').length
    })

    const isManualInputValid = computed(() => {
      const batchNo = manualInput.value.batchNo || ''
      const supplierCode = manualInput.value.supplierCode || ''
      const materialNo = manualInput.value.materialNo || ''
      
      return !!(
        batchNo.trim() &&
        supplierCode.trim() &&
        manualInput.value.supplierId &&
        materialNo.trim() &&
        manualInput.value.materialId &&
        manualInput.value.materialGroupId &&
        manualInput.value.wareInNum >= 1
      )
    })

    const canSubmit = computed(() => {
      return parsedList.value.length > 0
    })

    // 定时器变量
    let parseTimer = null

    // 加载仓库列表
    const fetchWarehouses = async () => {
      isLoadingWarehouses.value = true
      try {
        const res = await stockInApi.getWarehouses({
          current: 1,
          size: 100,
          bizDept: parsedList.value.length > 0 ? parsedList.value[0].bizDept : '',
          keyword: ''
        })
        if (res.success && Array.isArray(res.data.records)) {
          warehouseList.value = res.data.records.filter(ware => ware.status === 1)
          if (warehouseList.value.length > 0) {
            selectedWarehouseId.value = warehouseList.value[0].id
          }
        } else {
          warehouseList.value = []
          setStatus('获取仓库列表失败，请刷新页面重试', 'error')
        }
      } catch (err) {
        warehouseList.value = []
        setStatus(`仓库列表加载失败：${err.message || '网络异常'}`, 'error')
        console.error('fetchWarehouses error:', err)
      } finally {
        isLoadingWarehouses.value = false
      }
    }

    // 打开仓库选择弹窗
    const openWarehouseSelectModal = async () => {
      await fetchWarehouses()
      showWarehouseSelectModal.value = true
      setTimeout(() => {
        document.getElementById('warehouseSelect')?.focus()
      }, 300)
    }

    // 确认选择仓库
    const confirmWarehouseSelect = () => {
      selectedWarehouse.value = warehouseList.value.find(w => w.id === selectedWarehouseId.value);
      showWarehouseSelectModal.value = false;
      
      if (selectedWarehouse.value) {
        openPreview();
      }
    }

    // 解析扫描数据
    const handleSourceParse = async (singleData = null) => {
      if (isParsing.value) return
      isParsing.value = true

      try {
        await nextTick()
        const cleanData = singleData ? singleData.trim() : (sourceData.value || '').trim()
        if (!cleanData) {
          setStatus('请输入有效的扫描数据', 'warn')
          return
        }

        if (cleanData.includes('\n') || cleanData.includes('\r')) {
          setStatus('解析失败：数据包含多条记录，请分开扫描', 'error')
          return
        }

        const parseResult = await parseSourceData(cleanData)
        if (!parseResult.success) {
          if (parseResult.errorType === 'supplier') {
            supplierNotFoundMsg.value = parseResult.errorMsg
            showSupplierNotFoundModal.value = true
          } else if (parseResult.errorType === 'material') {
            materialNotFoundMsg.value = parseResult.errorMsg
            showMaterialNotFoundModal.value = true
          } else {
            setStatus(`解析失败：${parseResult.errorMsg}`, 'error')
          }
          if (!singleData) sourceData.value = ''
          return
        }

        const isDuplicate = parsedList.value.some(item => item.batchNo === parseResult.data.batchNo);
        if (isDuplicate) {
          playErrorBeep();
          showErrorDialog(
            '重复添加提示',
            `箱号 ${parseResult.data.batchNo} 已添加，请勿重复解析`,
            '请检查扫码数据或选择其他箱号',
            '确定'
          );
          if (!singleData) sourceData.value = '';
          return;
        }

        parsedList.value.unshift(parseResult.data)
        playSuccessBeep()
        if (selectedWarehouse.value) {
          setStatus('请在提交前选择入库仓库', 'warn')
        } else {
          setStatus(`解析成功！已添加 ${parsedList.value.length} 箱（共 ${totalQuantity.value.toFixed(2)}）`, 'success')
        }
        if (!singleData) sourceData.value = ''
        document.getElementById('sourceDataInput')?.focus()
      } catch (err) {
        showErrorDialog(
            '数据校验失败',
            '检查输入框的数据是否完整',
            '请确保所有必填字段都已正确填写',
            '确定'
          )
        console.error('handleSourceParse error:', err)
      } finally {
        isParsing.value = false
      }
    }

    // 解析自定义格式数据
    const parseSourceData = async (data) => {
      try {
        if (typeof data !== 'string') {
          return {
            success: false,
            errorMsg: '解析失败：输入数据不是字符串'
          }
        }
        
        const fields = data.split('*')
        // if (fields.length !== 10) {
        //   return {
        //     success: false,
        //     errorMsg: `数据格式错误！需符合规定格式（当前拆分为${fields.length}个字段，正确应为10个）`
        //   }
        // }
        if (fields.length < 10 || fields.length > 12) {
          return {
            success: false,
            errorMsg: `数据格式错误！需符合规定格式（当前拆分为${fields.length}个字段，正确应为10-12个）`
          }
        }

        const materialNo = (fields[0] || '').trim()
        const wareInNumStr = (fields[1] || '').trim()
        const supplierCode = (fields[2] || '').trim()
        const batchNo = (fields[9] || '').trim()

        console.log(`解析扫码数据：物料${materialNo}，数量${wareInNumStr}，箱号${batchNo}`)

        if (!materialNo) return { success: false, errorMsg: '第1个字段【物料编码】不能为空' }
        if (!supplierCode) return { success: false, errorMsg: '第3个字段【供应商编码】不能为空' }
        if (!batchNo) return { success: false, errorMsg: '最后1个字段【箱号】不能为空' }
        if (isNaN(Number(wareInNumStr)) || Number(wareInNumStr) <= 0) {
          return { success: false, errorMsg: `第2个字段【入库数量】必须是正整数（当前值：${wareInNumStr}）` }
        }

        const validateResult = await validateSourceData(materialNo, supplierCode)
        if (!validateResult.success) {
          return validateResult
        }

        const existsResult = await stockInApi.checkMaterialExists({
          materialId: validateResult.materialId,
          batchNo: batchNo
        })

        if (existsResult.data && existsResult.data.length > 0) {
          playErrorBeep();
          return {
            success: false,
            errorMsg: `物料 ${materialNo}（批次：${batchNo}）已存在，请检查`,
            errorType: 'material'
          }
        }

        return {
          success: true,
          data: {
            materialNo,
            materialId: validateResult.materialId,
            materialGroupId: validateResult.materialGroupId,
            wareInNum: Number(wareInNumStr),
            supplierCode,
            supplierId: validateResult.supplierId,
            supplierName: validateResult.supplierName,
            batchNo,
            remark: '',
            bizDept: validateResult.bizDept
          }
        }
      } catch (err) {
        return {
          success: false,
          errorMsg: `数据解析异常：${err.message || '未知错误'}`
        }
      }
    }

    // 验证数据有效性
    const validateSourceData = async (materialNo, supplierCode) => {
      try {
        // 验证供应商是否存在
        const supplierRes = await stockInApi.getSupplierByCode(supplierCode)
        const supplierList = supplierRes.data?.records || []
        if (supplierList.length === 0) {
          playErrorBeep();
          return {
            success: false,
            errorMsg: `供应商编码「${supplierCode}」不存在`,
            errorType: 'supplier'
          }
        }
        
        const supplierId = supplierList[0].id
        const supplierName = supplierList[0].supplierName

        // 验证物料是否存在
        const materialRes = await stockInApi.getMaterialBySupplierAndNo(materialNo, supplierId)
        const materialList = materialRes.data?.records || []
        if (materialList.length === 0) {
          playErrorBeep();
          return {
            success: false,
            errorMsg: `物料编码「${materialNo}」不存在，或未关联供应商「${supplierCode}」`,
            errorType: 'material'
          }
        }

        const materialId = materialList[0].id
        const materialGroupId = materialList[0].materialGroupId || materialList[0].material_group_id

        if (!materialGroupId) {
          playErrorBeep();
          return {
            success: false,
            errorMsg: `物料编码「${materialNo}」未配置物料组信息`,
            errorType: 'material'
          }
        }

        return {
          success: true,
          supplierId,
          supplierName,
          bizDept: supplierList[0].bizDept || '',
          materialId,
          materialGroupId
        }
      } catch (err) {
        return {
          success: false,
          errorMsg: `数据验证失败：${err.message || '网络异常'}`
        }
      }
    }

    // 取消入库提交
    const cancelSubmission = () => {
      if (!canCancel.value) return
      
      if (window.confirm('确定要取消入库操作吗？已处理的部分将保留，未处理的将终止。')) {
        submissionAborted.value = true
        canCancel.value = false
        setStatus('正在终止入库操作...', 'info')
      }
    }

    // 确认提交入库
    const confirmStockIn = async () => {
      if (parsedList.value.length === 0 || !selectedWarehouse.value) {
        setStatus('请先完善入库数据和仓库选择', 'warn');
        return;
      }

      // 数据校验
      const invalidItems = parsedList.value.filter(item => {
        return !item.materialId || !item.wareInNum || item.wareInNum <= 0 || !item.materialGroupId;
      });
      if (invalidItems.length > 0) {
        const errorMsg = invalidItems.map(item => 
          `箱号 ${item.batchNo}：${!item.materialId ? '物料ID为空' : 
            !item.materialGroupId ? '物料组ID为空' : '数量必须大于0'}`
        ).join('；');
        setStatus(`数据验证失败：${errorMsg}`, 'error');
        return;
      }

      // 按供应商ID分组
      const supplierGroups = {};
      parsedList.value.forEach(item => {
        const supplierId = item.supplierId;
        if (!supplierGroups[supplierId]) {
          supplierGroups[supplierId] = {
            id: supplierId,
            name: item.supplierName || item.supplierCode,
            items: []
          };
        }
        supplierGroups[supplierId].items.push(item);
      });
      
      const supplierList = Object.values(supplierGroups);
      totalSuppliers.value = supplierList.length;
      
      if (totalSuppliers.value === 0) {
        setStatus('未检测到有效供应商数据', 'error');
        return;
      }

      // 初始化进度跟踪
      processingProgress.value = supplierList.map(supplier => ({
        supplierId: supplier.id,
        supplierName: supplier.name,
        batchCount: supplier.items.length,
        status: 'pending',
        progress: 0,
        errorMsg: ''
      }));
      
      currentSuccessCount.value = 0;
      currentProcessingCount.value = 0;
      currentErrorCount.value = 0;
      submissionAborted.value = false;
      canCancel.value = true;

      // 确认提交
      const confirmMsg = `共检测到 ${totalSuppliers.value} 个供应商的数据，将分 ${totalSuppliers.value} 次提交。`;
      if (!window.confirm(confirmMsg)) {
        return;
      }

      isSubmitting.value = true;
      showPreview.value = false;
      setStatus('正在提交入库数据，请稍候...', 'info');

      try {
        // 遍历所有供应商，按顺序执行接口
        for (let i = 0; i < supplierList.length; i++) {
          if (submissionAborted.value) {
            setStatus(`入库操作已取消，已处理 ${i}/${totalSuppliers.value} 个供应商`, 'warn');
            break;
          }
          
          const supplier = supplierList[i];
          const progressIndex = processingProgress.value.findIndex(p => p.supplierId === supplier.id);
          
          // 更新当前供应商状态为处理中
          if (progressIndex !== -1) {
            processingProgress.value[progressIndex].status = 'processing';
            processingProgress.value[progressIndex].progress = 30;
            currentProcessingCount.value++;
          }
          
          // 非首次请求：执行等待
          if (i > 0) {
            setStatus(`正在处理第${i+1}/${totalSuppliers.value}个供应商数据...`, 'info');
            await new Promise(resolve => setTimeout(resolve, 5000));
          }
          
          try {
            // 处理当前供应商
            await processSupplierInBackground(supplier.id, supplier.items);
            
            // 更新进度为成功
            if (progressIndex !== -1) {
              processingProgress.value[progressIndex].status = 'success';
              processingProgress.value[progressIndex].progress = 100;
              currentSuccessCount.value++;
            }
          } catch (error) {
            // 更新进度为失败
            if (progressIndex !== -1) {
              processingProgress.value[progressIndex].status = 'error';
              processingProgress.value[progressIndex].progress = 100;
              processingProgress.value[progressIndex].errorMsg = error.message || '处理失败';
              currentErrorCount.value++;
            }
            console.error(`处理供应商${supplier.name}失败:`, error);
          } finally {
            // 减少处理中计数
            if (progressIndex !== -1) {
              currentProcessingCount.value--;
            }
          }
        }
        
        // 所有任务处理完成后更新状态
        if (submissionAborted.value) {
          setStatus(`入库操作已取消。成功: ${currentSuccessCount.value}, 失败: ${currentErrorCount.value}`, 'warn');
        } else {
          setStatus(`所有入库任务已处理完成。成功: ${currentSuccessCount.value}, 失败: ${currentErrorCount.value}`, 'success');
        }
        
        // 清空列表
        parsedList.value = [];
        
        // 跳转到状态查看页面
        setTimeout(() => {
          isSubmitting.value = false;
          openTaskStatusModal();
        }, 1000);
      } catch (err) {
        console.error('入库任务处理异常:', err);
        setStatus('部分入库任务处理异常，可查看入库状态了解详情', 'warn');
        isSubmitting.value = false;
      }
    };

    // 执行单个供应商的入库接口链
    const processSupplierInBackground = async (supplierId, groupData) => {
      const firstItem = groupData[0];
      let currentWareInId = null;

      try {
        // 构建提交参数
        const submitData = {
          $wareInType: "0",
          wareInType: "0",
          storeId: selectedWarehouse.value.id,
          supplierId: supplierId,
          supplierName: firstItem.supplierName || '未知供应商',
          wareInDetailList: groupData.map(item => ({
            materialId: item.materialId,
            materialGroupId: item.materialGroupId,
            wareInNum: item.wareInNum.toFixed(2),
            imageRemark: "",
            remark: item.remark || "",
            batchNo: item.batchNo
          }))
        };

        // 调用saveStockIn接口
        console.log(`[处理中] 供应商${supplierId}开始提交入库单`);
        const saveRes = await stockInApi.saveStockIn(submitData);
        if (!saveRes.success) throw new Error(saveRes.msg || '保存入库单失败');
        
        currentWareInId = saveRes.data.wareInId;
        if (!currentWareInId) throw new Error('未获取到入库单ID');
        
        // 记录任务状态
        if (!backgroundTaskIds.value.includes(currentWareInId)) {
          backgroundTaskIds.value.push(currentWareInId);
        }
        backgroundTaskStatus.value[currentWareInId] = 'processing';
        taskErrors.value[currentWareInId] = '';

        // 调用distributeStockIn接口
        await stockInApi.distributeStockIn(currentWareInId);

        // 调用collectStockIn接口
        const collectData = {
          wareInId: currentWareInId,
          materialId: submitData.wareInDetailList[0]?.materialId || '',
          materialGroupId: submitData.wareInDetailList[0]?.materialGroupId || '',
          type: 0,
          wareInNum: submitData.wareInDetailList[0]?.wareInNum || 0,
          wareInDetailRecordVOList: submitData.wareInDetailList.map(item => ({
            materialId: item.materialId,
            wareInNum: item.wareInNum,
            // palletCode: '0003',
            palletCode: '整箱出库',
            batchNo: item.batchNo
          }))
        };
        await stockInApi.collectStockIn(collectData);

        // 调用confirmCollect接口
        await stockInApi.confirmCollect({ id: currentWareInId });

        // 标记任务成功
        backgroundTaskStatus.value[currentWareInId] = 'success';
        console.log(`[处理成功] 供应商${supplierId}入库单${currentWareInId}执行成功`);

      } catch (error) {
        const errorMsg = `处理失败：${error.message}`;
        console.error(`[处理失败] 供应商${supplierId}处理异常:`, errorMsg);
        
        // 记录错误信息
        if (currentWareInId) {
          backgroundTaskStatus.value[currentWareInId] = 'error';
          taskErrors.value[currentWareInId] = errorMsg;
        } else {
          throw new Error(`供应商${firstItem.supplierName || supplierId}提交失败: ${error.message}`);
        }
      }
    };

    // 刷新入库任务状态
    const refreshTaskStatus = async () => {
      if (backgroundTaskIds.value.length === 0) {
        setStatus('暂无入库任务记录', 'warn');
        return;
      }

      isRefreshingStatus.value = true;
      try {
        const res = await stockInApi.batchQueryStockInStatus({
          wareInIds: backgroundTaskIds.value
        });

        if (res.success && Array.isArray(res.data)) {
          res.data.forEach(task => {
            backgroundTaskStatus.value[task.wareInId] = task.status;
            
            if (task.status === 'error' && task.errorMsg) {
              taskErrors.value[task.wareInId] = task.errorMsg;
            }
          });
          setStatus('入库状态已更新', 'success');
        }
      } catch (err) {
        // setStatus(`刷新状态失败：${err.message || '网络异常'}`, 'error');
        console.error('refreshTaskStatus error:', err);
      } finally {
        isRefreshingStatus.value = false;
      }
    };

    // 设置状态提示
    const setStatus = (message, type = 'success') => {
      statusMsg.value = message
      statusType.value = type
      setTimeout(() => {
        if (statusMsg.value === message) {
          statusMsg.value = ''
        }
      }, 3000)
    }

    // 处理输入变化
    const handleInputChange = () => {
      if (parseTimer) clearTimeout(parseTimer)
      const currentData = (sourceData.value || '').trim()
      
      if (currentData.includes('\n') || currentData.includes('\r')) {
        const lineBreakMatch = currentData.match(/[\n\r]/)
        if (lineBreakMatch) {
          const firstData = currentData.substring(0, lineBreakMatch.index).trim()
          if (firstData) {
            if (parseTimer) clearTimeout(parseTimer)
            sourceData.value = currentData.substring(lineBreakMatch.index + 1).trim()
            handleSourceParse(firstData)
          }
        }
      } else if (currentData.length > 15) {
        parseTimer = setTimeout(() => {
          handleSourceParse()
        }, 300)
      }
    }

    // 清空扫描输入框
    const clearSourceData = () => {
      sourceData.value = ''
      if (parseTimer) clearTimeout(parseTimer)
      document.getElementById('sourceDataInput')?.focus()
    }

    // 删除一条解析后的数据
    const removeCard = (index) => {
      const removedItem = parsedList.value.splice(index, 1)[0]
      setStatus(`已删除箱号 ${removedItem.batchNo} 的数据`, 'warn')
    }

    // 手动输入：清除字段空格
    const trimInput = (field) => {
      if (typeof manualInput.value[field] === 'string') {
        manualInput.value[field] = manualInput.value[field].trim()
      } else {
        manualInput.value[field] = (manualInput.value[field] || '').toString().trim()
      }
    }

    // 格式化入库数量
    const formatWareInNum = () => {
      let num = Number(manualInput.value.wareInNum)
      if (isNaN(num) || num < 0) num = 0
      manualInput.value.wareInNum = Number(num.toFixed(2))
    }

    // 供应商编码失焦时查询
    const handleSupplierCodeBlur = async () => {
      const code = (manualInput.value.supplierCode || '').trim()
      if (!code) {
        manualInput.value.supplierId = ''
        return
      }

      try {
        const res = await stockInApi.getSupplierByCode(code)
        const supplierList = res.data?.records || []
        if (supplierList.length > 0) {
          manualInput.value.supplierId = supplierList[0].id
        } else {
          manualInput.value.supplierId = ''
          supplierNotFoundMsg.value = `供应商编码「${code}」不存在`
          showSupplierNotFoundModal.value = true
        }
      } catch (err) {
        console.error('查询供应商失败:', err)
        setStatus('查询供应商信息失败，请稍后重试', 'error')
      }
    }

    // 物料编码失焦时查询
    const handleMaterialNoBlur = async () => {
      const materialNo = (manualInput.value.materialNo || '').trim()
      const supplierId = manualInput.value.supplierId || ''
      
      if (!materialNo || !supplierId) {
        manualInput.value.materialId = ''
        manualInput.value.materialGroupId = ''
        return
      }

      try {
        const res = await stockInApi.getMaterialBySupplierAndNo(materialNo, supplierId)
        const materialList = res.data?.records || []
        
        if (materialList.length > 0) {
          manualInput.value.materialId = materialList[0].id
          manualInput.value.materialGroupId = materialList[0].materialGroupId || 
                                             materialList[0].material_group_id
                                           
          if (!manualInput.value.materialGroupId) {
            materialNotFoundMsg.value = `物料编码「${materialNo}」未配置物料组信息`
            showMaterialNotFoundModal.value = true
          }
        } else {
          manualInput.value.materialId = ''
          manualInput.value.materialGroupId = ''
          materialNotFoundMsg.value = `物料编码「${materialNo}」不存在或未关联当前供应商`
          showMaterialNotFoundModal.value = true
        }
      } catch (err) {
        console.error('查询物料失败:', err)
        setStatus('查询物料信息失败，请稍后重试', 'error')
      }
    }

    // 打开手动输入弹窗
    const openManualInput = () => {
      manualInput.value = {
        materialNo: '',
        materialId: '',
        materialGroupId: '',
        wareInNum: 1.00,
        supplierCode: '',
        supplierId: '',
        batchNo: '',
        remark: ''
      }
      showManualInput.value = true
      setTimeout(() => {
        document.getElementById('materialNo')?.focus()
      }, 300)
    }

    // 确认手动输入数据
    const confirmManualInput = () => {
      if (!isManualInputValid.value) {
        setStatus('请完善所有必填项，确保物料和供应商信息有效', 'warn')
        return
      }

      const batchNo = (manualInput.value.batchNo || '').trim()
      const isDuplicate = parsedList.value.some(
        item => (item.batchNo || '').trim() === batchNo
      )
      if (isDuplicate) {
        setStatus(`箱号 ${batchNo} 已存在，请勿重复添加`, 'warn')
        return
      }

      parsedList.value.push({
        materialNo: manualInput.value.materialNo,
        materialId: manualInput.value.materialId,
        materialGroupId: manualInput.value.materialGroupId,
        wareInNum: manualInput.value.wareInNum,
        supplierCode: manualInput.value.supplierCode,
        supplierId: manualInput.value.supplierId,
        supplierName: '',
        batchNo: manualInput.value.batchNo,
        remark: manualInput.value.remark
      })

      setStatus(`手动添加成功！当前共 ${parsedList.value.length} 箱（${totalQuantity.value.toFixed(2)}）`, 'success')
      showManualInput.value = false
      document.getElementById('sourceDataInput')?.focus()
    }

    // 打开预览弹窗
    const openPreview = () => {
      if (parsedList.value.length === 0) {
        setStatus('暂无入库数据可预览', 'warn');
        return;
      }
      if (selectedWarehouse.value) {
        showPreview.value = true;
      }
    }

    // 提交按钮点击处理
    const handleSubmit = () => {
      if (!selectedWarehouse.value) {
        openWarehouseSelectModal();
      } else {
        openPreview();
      }
    }

    // 入库状态弹窗控制
    const openTaskStatusModal = () => {
      showTaskStatusModal.value = true;
      setTimeout(() => {
        refreshTaskStatus();
      }, 300);
    };
    
    const closeTaskStatusModal = () => {
      showTaskStatusModal.value = false;
    };

    // 其他弹窗关闭方法
    const closeManualInput = () => showManualInput.value = false
    const closePreview = () => showPreview.value = false
    const closeSupplierNotFoundModal = () => showSupplierNotFoundModal.value = false
    const closeMaterialNotFoundModal = () => showMaterialNotFoundModal.value = false
    const closeWarehouseSelectModal = () => showWarehouseSelectModal.value = false
    const reselectWarehouse = () => openWarehouseSelectModal()
    const handleBack = () => router.back()

    // 处理错误弹窗的确认操作
    const handleErrorModalAction = () => {
    showErrorModal.value = false
      if (typeof errorModalAction.value === 'function') {
        errorModalAction.value()
      }
      // 添加焦点处理，确保弹窗关闭后焦点回到输入框
      setTimeout(() => {
        document.getElementById('sourceDataInput')?.focus()
      }, 100)
    }

    // 组件初始化
    onMounted(() => {
      document.getElementById('sourceDataInput')?.focus()
      document.addEventListener('click', initAudioContext, { once: true })
      document.addEventListener('touchstart', initAudioContext, { once: true })
    })

    // 监听手动输入弹窗关闭
    watch(() => showManualInput.value, (newVal) => {
      if (!newVal && parseTimer) {
        clearTimeout(parseTimer)
      }
    })

    return {
      sourceData,
      parsedList,
      statusMsg,
      statusType,
      isParsing,
      isSubmitting,
      showManualInput,
      showPreview,
      showSupplierNotFoundModal,
      showMaterialNotFoundModal,
      showWarehouseSelectModal,
      showErrorModal,
      errorModalTitle,
      errorModalDetail,
      errorModalAdvice,
      errorModalActionText,
      isLoadingWarehouses,
      manualInput,
      supplierNotFoundMsg,
      materialNotFoundMsg,
      warehouseList,
      selectedWarehouse,
      selectedWarehouseId,
      totalQuantity,
      uniqueMaterialCount,
      materialDetails, // 添加到返回对象中
      uniqueSupplierCount,
      groupedSummary,
      isManualInputValid,
      canSubmit,
      currentDateTime,
      currentUser,
      processingProgress,
      totalSuppliers,
      currentSuccessCount,
      currentProcessingCount,
      currentErrorCount,
      canCancel,
      backgroundTaskIds,
      backgroundTaskStatus,
      taskErrors,
      showTaskStatusModal,
      isRefreshingStatus,
      successTaskCount,
      processingTaskCount,
      errorTaskCount,
      handleSourceParse,
      handleInputChange,
      clearSourceData,
      removeCard,
      openManualInput,
      closeManualInput,
      confirmManualInput,
      trimInput,
      handleSupplierCodeBlur,
      handleMaterialNoBlur,
      openPreview,
      closePreview,
      confirmStockIn,
      showErrorDialog,
      closeSupplierNotFoundModal,
      closeMaterialNotFoundModal,
      openWarehouseSelectModal,
      closeWarehouseSelectModal,
      confirmWarehouseSelect,
      reselectWarehouse,
      handleBack,
      handleSubmit,
      playErrorBeep,
      openTaskStatusModal,
      closeTaskStatusModal,
      refreshTaskStatus,
      formatWareInNum,
      cancelSubmission,
      handleErrorModalAction
    }
  }
}
</script>






<style scoped>
/* 基础容器样式 */
.progress-modal {
  width: 90vw;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

/* 物料详情样式 */
.material-details {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 200px;
}

.material-item {
  background-color: var(--card-background);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  color: var(--text-secondary);

  width: 90vw;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.progress-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.progress-stats {
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-color);
  flex-wrap: wrap;
  gap: 8px;
}

.stat-item {
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.stat-item.success {
  background-color: rgba(16, 185, 129, 0.12);
  color: #10b981;
}

.stat-item.processing {
  background-color: rgba(245, 158, 11, 0.12);
  color: #f59e0b;
}

.stat-item.error {
  background-color: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}

.stat-item.total {
  background-color: rgba(35, 177, 192, 0.12);
  color: var(--primary-color);
}

.stat-icon {
  font-size: 16px;
}

.progress-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.progress-item {
  padding: 12px;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  transition: all 0.3s;
}

.progress-item.processing {
  border-color: #f59e0b;
  background-color: rgba(245, 158, 11, 0.12);
}

.progress-item.success {
  border-color: #10b981;
  background-color: rgba(16, 185, 129, 0.12);
}

.progress-item.error {
  border-color: #ef4444;
  background-color: rgba(239, 68, 68, 0.12);
}

.progress-item-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-weight: 500;
}

.supplier-name {
  color: var(--text-primary);
}

.status-badge {
  font-size: 13px;
  padding: 2px 8px;
  border-radius: 4px;
}

.progress-item.processing .status-badge {
  background-color: rgba(245, 158, 11, 0.2);
  color: #f59e0b;
}

.progress-item.success .status-badge {
  background-color: rgba(16, 185, 129, 0.2);
  color: #10b981;
}

.progress-item.error .status-badge {
  background-color: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

.batch-info {
  font-size: 13px;
  color: var(--text-secondary);
  margin-bottom: 8px;
}

.error-message {
  font-size: 13px;
  color: #ef4444;
  padding: 8px;
  background-color: rgba(239, 68, 68, 0.12);
  border-radius: 4px;
  margin-top: 8px;
}

.progress-bar {
  height: 6px;
  background-color: var(--card-background);
  border-radius: 3px;
  overflow: hidden;
  margin-top: 8px;
}

.progress-indicator {
  height: 100%;
  background-color: #f59e0b;
  transition: width 0.3s ease;
}
.scan-stock-in-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
  background-color: var(--background-color);
  color: var(--text-primary);
  font-size: 14px;
}

/* 头部样式 */
.header {
  display: flex;
  align-items: center;
  padding: 3px;
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px var(--shadow-light);
  position: sticky;
  top: 0;
  z-index: 100;
}

.back-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px; /* 调整内边距来控制按钮整体大小 */
  border-radius: 4px; /* 可选：添加圆角 */
}

.icon-back {
  width: 24px; /* 调整为你需要的大小 */
  height: 24px; /* 调整为你需要的大小 */
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.back-btn:focus {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}

.title {
  flex: 1;
  font-size: 18px;
  text-align: center;
  margin: 0;
  font-weight: 600;
}

/* 内容区域样式 */
.content {
  flex: 1;
  padding: 12px; /* 减少内边距 */
  overflow-y: auto;
  box-sizing: border-box;
  padding-bottom: 80px;
}

/* 状态提示样式 */
.status-banner {
  padding: 12px;
  margin-bottom: 16px;
  border-radius: 8px;
  font-size: 14px;
  text-align: center;
  font-weight: 500;
  transition: opacity 0.3s;
}

.status-banner.success {
  background-color: rgba(16, 185, 129, 0.12);
  color: #10b981;
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.status-banner.error {
  background-color: rgba(239, 68, 68, 0.12);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.status-banner.warn {
  background-color: rgba(245, 158, 11, 0.12);
  color: #f59e0b;
  border: 1px solid rgba(245, 158, 11, 0.3);
}

/* 表单区域样式 */
.form-section {
  background: var(--card-background);
  border-radius: 8px;
  padding: 12px; /* 优化内边距 */
  margin-bottom: 12px; /* 减少外边距 */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08); /* 减轻阴影提升性能 */
}

.section-title {
  font-size: 16px;
  color: var(--text-primary);
  margin-bottom: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.section-title::before {
  content: '';
  width: 4px;
  height: 18px;
  background-color: var(--primary-color);
  margin-right: 8px;
  border-radius: 2px;
}

/* 仓库信息栏样式 */
.warehouse-info-bar {
  padding: 10px; /* 减少内边距 */
  background-color: rgba(35, 177, 192, 0.12);
  border-radius: 8px;
  margin-bottom: 12px; /* 减少间距 */
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid rgba(35, 177, 192, 0.2);
}

.warehouse-label {
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
}

.warehouse-value {
  color: var(--primary-color);
  font-weight: 600;
  flex: 1;
  margin: 0 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reselect-btn {
  background-color: var(--primary-color);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
  height: 32px;
  transition: background-color 0.2s;
}

.reselect-btn:hover {
  background-color: #16a3b8;
}

/* 输入控件样式 */
.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
}

#sourceDataInput, .form-group input, .warehouse-select {
  width: 100%;
  padding: 14px 16px; /* 增加输入框内边距 */
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 16px; /* 增大输入字体 */
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  min-height: 48px; /* 提高输入框高度 */
  background-color: var(--card-background);
  color: var(--text-primary);
}

#sourceDataInput:focus, .form-group input:focus, .warehouse-select:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(35, 177, 192, 0.2);
}

#sourceDataInput {
  resize: none;
}

/* 仓库选择框样式 */
.warehouse-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 16px center;
  background-size: 16px;
  padding-right: 40px;
}

/* 按钮样式 */
.btn {
  min-height: 44px; /* 增大触摸区域至44x44px标准 */
  border-radius: 8px;
  font-size: 16px; /* 增大字体提高可读性 */
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px; /* 增加内边距 */
  font-weight: 600; /* 加粗字体 */
  transition: background-color 0.2s;
}

/* 按钮布局 */
.source-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 8px;
}

.source-actions .parse-btn {
  grid-column: 1 / 2;
}

.source-actions .manual-input-btn {
  grid-column: 2 / 3;
}

.source-actions .clear-btn {
  grid-column: 1 / 3;
}

/* 按钮颜色区分 */
.parse-btn {
  background-color: var(--primary-color);
  color: #fff;
}

.parse-btn:disabled {
  background-color: rgba(35, 177, 192, 0.5);
  cursor: not-allowed;
}

.parse-btn:not(:disabled):hover {
  background-color: #16a3b8;
}

.manual-input-btn {
  background-color: #f59e0b;
  color: #fff;
}

.manual-input-btn:hover {
  background-color: #d48806;
}

.clear-btn {
  background-color: #f5f7fa;
  color: #666;
  border: 1px solid #dcdfe6;
}

.clear-btn:hover {
  background-color: #eef0f3;
}

.submit-btn {
  width: 100%;
  background-color: #42b983;
  color: #fff;
  margin-top: 8px;
  font-size: 15px;
  /* 新增固定定位样式 */
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 32px);
  max-width: 500px;
  z-index: 90;
  padding: 8px 0;
}

.submit-btn:disabled {
  background-color: #a0d4ae;
  cursor: not-allowed;
}

.submit-btn:not(:disabled):hover {
  background-color: #359469;
}

/* 摘要栏样式 */
.summary-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px;
  background-color: #f9fafb;
  border-radius: 8px;
  margin-bottom: 16px;
  border: 1px solid #f0f2f5;
}

.summary-item {
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.summary-label {
  color: #666;
  margin-right: 6px;
}

.summary-value {
  color: #333;
}

/* 空状态样式 */
.empty-state {
  padding: 48px 0;
  text-align: center;
  color: #999;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.4;
}

.empty-text {
  font-size: 14px;
  line-height: 1.5;
}

/* 数据卡片列表样式 - 入库明细卡片 */
.card-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 16px;
}

.data-card {
  border: 1px solid #f0f2f5;
  border-radius: 8px;
  padding: 12px; /* 减少内边距 */
  margin-bottom: 10px; /* 优化间距 */
  transition: box-shadow 0.2s;
  background-color: #fff;
}

.data-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f2f5;
}

.card-label {
  font-size: 16px; /* 增大标签字体 */
  color: #1890ff;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 80%;
}

.card-delete {
  background: transparent;
  border: none;
  color: #999;
  font-size: 18px;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s, color 0.2s;
}

.card-delete:hover {
  background-color: #fff1f0;
  color: #f5222d;
}

.card-body {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.card-row {
  font-size: 15px; /* 增大内容字体 */
  line-height: 1.6; /* 优化行高 */
  display: flex;
}

.row-label {
  color: #666;
  min-width: 80px;
  font-weight: 500;
}

.row-value {
  color: #333;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.text-primary {
  color: #42b983;
  font-weight: 600;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
  backdrop-filter: blur(2px);
}

.modal-container {
  width: 100%;
  max-width: 500px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  animation: modalFadeIn 0.3s;
}

/* PDA优化样式 */
.pda-optimized {
  max-height: 95vh;
  max-width: 95vw;
  padding: 0;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #f0f2f5;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: #333;
}

.modal-close {
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #999;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s, color 0.2s;
}

.modal-close:hover {
  background-color: #f5f7fa;
  color: #333;
}

.modal-close:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.modal-body {
  padding: 16px;
  overflow-y: auto;
  max-height: calc(95vh - 120px);
  box-sizing: border-box;
}

.modal-footer {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid #f0f2f5;
}

.modal-footer .btn {
  flex: 1;
}

.modal-cancel {
  background-color: #f5f7fa;
  color: #666;
  border: 1px solid #dcdfe6;
}

.modal-cancel:hover {
  background-color: #eef0f3;
}

.modal-confirm {
  background-color: #42b983;
  color: #fff;
}

.modal-confirm:disabled {
  background-color: #a0d4ae;
  cursor: not-allowed;
}

.modal-confirm:not(:disabled):hover {
  background-color: #359469;
}

/* 新增：错误弹窗样式 */
.error-modal .modal-header {
  background-color: #fff1f0;
  border-bottom: 1px solid #ffe5e0;
}

.error-modal .modal-title {
  color: #f5222d;
}

.error-body {
  padding: 24px;
  text-align: center;
}

.error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.error-title {
  font-size: 16px;
  color: #f5222d;
  margin-bottom: 8px;
  font-weight: 500;
}

.error-advice {
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

.error-confirm {
  background-color: #f5222d;
  color: #fff;
}

.error-confirm:hover {
  background-color: #d91e18;
}

/* 错误提示文本 */
.error-message {
  color: #f5222d;
  font-size: 14px;
  margin: 0 0 8px 0;
  text-align: center;
}

.error-tip {
  color: #999;
  font-size: 13px;
  margin: 0;
  text-align: center;
}

/* 无仓库提示文本 */
.empty-warehouse-text {
  color: #f5222d;
  font-size: 13px;
  margin-top: 8px;
  text-align: center;
  line-height: 1.5;
}

/* 预览弹窗样式 - 入库汇总表格样式 */
.preview-summary {
  background-color: #f9fafb;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  border: 1px solid #f0f2f5;
}

.preview-summary .summary-title {
  font-size: 16px;
  margin-bottom: 16px;
  color: #333;
  font-weight: 600;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f2f5;
}

/* 汇总表格容器 */
.summary-table-container {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 16px;
}

/* 汇总表格样式 */
.summary-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  background-color: #fff;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.summary-table th {
  background-color: #f5f7fa;
  color: #666;
  font-weight: 600;
  padding: 12px 8px;
  text-align: left;
  border-bottom: 1px solid #f0f2f5;
  font-size: 13px;
  position: sticky;
  top: 0;
  z-index: 10;
}

/* 列宽分配 */
.summary-header:nth-child(1) { width: 30%; }
.summary-header:nth-child(2) { width: 30%; }
.summary-header:nth-child(3) { width: 20%; text-align: center; }
.summary-header:nth-child(4) { width: 20%; text-align: center; }

.summary-table td {
  padding: 12px 8px;
  border-bottom: 1px solid #f0f2f5;
  font-size: 13px;
  word-wrap: break-word;
  word-break: break-all;
}

.summary-row:last-child td {
  border-bottom: none;
}

/* 特殊单元格样式 */
.supplier-cell { color: #1890ff; }
.material-cell { color: #333; }
.total-cell { color: #42b983; font-weight: 600; text-align: center; }
.summary-row td:nth-child(3) { text-align: center; }

/* 总计行样式 */
.total-row {
  background-color: #f6ffed;
  font-weight: 600;
}

/* 仓库信息摘要 */
.warehouse-info-summary {
  font-size: 13px;
  color: #666;
  background-color: #fff;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #f0f2f5;
}

.warehouse-info-summary p {
  margin: 6px 0;
  line-height: 1.5;
}

/* 入库明细卡片样式 */
.preview-detail .detail-title {
  font-size: 16px;
  margin-bottom: 16px;
  color: #333;
  font-weight: 600;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f2f5;
}

.detail-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}

.detail-card {
  border: 1px solid #f0f2f5;
  border-radius: 8px;
  overflow: hidden;
  background-color: #fff;
  transition: box-shadow 0.2s;
}

.detail-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.detail-card-header {
  background-color: #f5f7fa;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f2f5;
}

.detail-card-title {
  font-size: 14px;
  color: #1890ff;
  font-weight: 600;
}

.detail-card-body {
  padding: 12px 16px;
}

.detail-item {
  display: flex;
  margin-bottom: 8px;
  font-size: 13px;
  line-height: 1.5;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-label {
  color: #666;
  min-width: 80px;
  font-weight: 500;
}

.detail-value {
  color: #333;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.highlight-item {
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px dashed #e0e0e0;
}

.highlight-item .detail-value {
  color: #42b983;
  font-weight: 600;
}

/* 必填项标记 */
.required {
  color: #f5222d;
  margin-left: 2px;
}

/* 响应式适配 - PDA小屏 */
@media (max-width: 480px) {
  .source-actions {
    grid-template-columns: 1fr;
  }
  
  .source-actions .parse-btn,
  .source-actions .manual-input-btn,
  .source-actions .clear-btn {
    grid-column: 1 / 2;
    width: 100%;
  }
  
  .card-row {
    flex-wrap: wrap;
  }
  
  .row-label {
    min-width: auto;
    margin-right: 8px;
    margin-bottom: 4px;
  }

  .warehouse-info-bar {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .reselect-btn {
    align-self: flex-end;
  }

  .summary-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  .detail-cards {
    grid-template-columns: 1fr;
  }
  
  /* 表格在小屏幕上的优化 */
  .summary-table th,
  .summary-table td {
    padding: 10px 6px;
    font-size: 12px;
  }
  
  .summary-header:nth-child(1) { width: 28%; }
  .summary-header:nth-child(2) { width: 28%; }
  .summary-header:nth-child(3) { width: 22%; }
  .summary-header:nth-child(4) { width: 22%; }
  
  .modal-body {
    padding: 12px;
    max-height: calc(95vh - 100px);
  }
  
  .modal-header, .modal-footer {
    padding: 12px;
  }
  
  .preview-summary {
    padding: 12px;
  }
}
/* 新增：加载动画样式 */
.loading-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 300px;
  height: 200px;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.loading-text {
  color: #333;
  font-size: 16px;
  text-align: center;
  padding: 0 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
    